<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { Keyboard, Navigation, Thumbs } from "swiper/modules";
import Product from "~/models/Product";
import { Swiper, SwiperSlide } from "swiper/vue";
import "swiper/swiper-bundle.css";
import type { Swiper as SwiperType } from "swiper/types";
import ProductImage from "~/models/ProductImage";
import {
  breakpointsBootstrapV5,
  useBreakpoints,
  useMounted,
} from "@vueuse/core";
import ProductWishlistButton from "@/components/product/ProductWishlistButton.vue";
import ProductShareButton from "~/components/common/product/ProductShareButton.vue";
import auth from "~/services/AuthService";
import type { Modal } from "bootstrap";

(() => Swiper)();
(() => SwiperSlide)();

const props = defineProps({
  product: {
    type: Product,
    required: true,
  },
});

const modules = [Keyboard, Navigation, Thumbs];

const mainSwiper = ref<SwiperType>();
const setMainSwiper = (swiper: SwiperType) => {
  mainSwiper.value = swiper;
};

const thumbsSwiper = ref<SwiperType>();
const setThumbsSwiper = (swiper: SwiperType) => {
  thumbsSwiper.value = swiper;
};

const thumbImages = computed(() =>
  props.product.getImagesOfSize(ProductImage.Sizes.ExtraSmall),
);

const largeImages = computed(() =>
  props.product.getImagesOfSize(
    fullScreen.value ? ProductImage.Sizes.Original : ProductImage.Sizes.Medium,
  ),
);

const brandError = ref(false);
const ssrWidth = useResponsiveSsrWidth();
const breakpoints = useBreakpoints(breakpointsBootstrapV5, { ssrWidth });
const isMobileSize = breakpoints.smallerOrEqual("md");
const desktopThumbSpaceBetween = 5;
const responsiveSlideCount = computed((): number => {
  return Math.min(thumbsCount.value, 6);
});
const thumbsSlidesPerView = computed((): number | "auto" => {
  return isMobileSize.value ? "auto" : responsiveSlideCount.value;
});
const thumbsContainerHeight = computed((): string => {
  if (isMobileSize.value) {
    return "calc(var(--thumb-size) + 2px)";
  }

  const visibleSlides = responsiveSlideCount.value;
  const gaps = Math.max(visibleSlides - 1, 0);

  return `calc(((var(--thumb-size) + 2px) * ${visibleSlides}) + (${desktopThumbSpaceBetween}px * ${gaps}))`;
});
const fullScreen = ref(false);
const galleryModalEl = ref<HTMLElement>();
const galleryModal = ref<Modal>();
const isUserLogged = ref(false);
const galleryContainer = ref<HTMLDivElement>();
const galleryModalBody = ref<HTMLDivElement>();
const gallerySection = ref<HTMLElement>();

onMounted(async () => {
  isUserLogged.value = auth.isUserLogged();
  if (galleryModalEl.value) {
    const { Modal } = await import("bootstrap");
    galleryModal.value = new Modal(galleryModalEl.value as Element);
    galleryModalEl.value.addEventListener("show.bs.modal", async () => {
      await nextTick();
      fullScreen.value = true;
    });
    galleryModalEl.value.addEventListener("hide.bs.modal", async () => {
      await nextTick();
      fullScreen.value = false;
    });
  }
  showVideoSlideIfVisible();
});

const toggleGalleryModal = () => {
  if (!galleryModal.value || fullScreen.value) {
    return;
  }
  const show = !fullScreen.value;
  if (show) {
    galleryModal.value?.show();
  } else {
    galleryModal.value?.hide();
  }
};

const videoSlideCt = ref<HTMLElement>();
const showVideoSlide = ref(false);
const onSlideChange = () => {
  showVideoSlideIfVisible();
};
const showVideoSlideIfVisible = () => {
  if (!mainSwiper.value || !videoSlideCt.value) {
    return;
  }
  const hasVideoEl = mainSwiper.value.slides[
    mainSwiper.value.activeIndex
  ]?.contains(videoSlideCt.value);
  if (!showVideoSlide.value && hasVideoEl) {
    showVideoSlide.value = true;
  }
};

const thumbsCount = computed(
  () => thumbImages.value.length + (props.product.video_link ? 1 : 0),
);
const isImageCover = (image: string) => {
  return props.product.getCoverImage() === image;
};

const mounted = useMounted();

// Lógica para mover la galería al modal de pantalla completa en sustitución de Teleport que crashea cuando se navega a otra página
watch(
  () => fullScreen.value,
  () => {
    if (
      galleryContainer.value &&
      galleryModalBody.value &&
      gallerySection.value
    ) {
      if (fullScreen.value) {
        galleryModalBody.value.append(galleryContainer.value);
      } else {
        gallerySection.value.append(galleryContainer.value);
      }
    }
  },
);
</script>

<template>
  <section ref="gallerySection" class="h-100 position-relative">
    <div ref="galleryModalEl" class="modal fade" tabindex="-1">
      <div class="modal-dialog modal-fullscreen">
        <div class="modal-content">
          <div class="modal-header py-1 py-lg-2">
            <h5 class="modal-title text-truncate">{{ product.name }}</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div
            ref="galleryModalBody"
            class="modal-body p-0 pt-1 p-lg-3 pt-lg-3"
          ></div>
          <div class="modal-footer justify-content-center py-1">
            <button
              type="button"
              class="btn btn-dark btn-sm"
              data-bs-dismiss="modal"
            >
              Salir de pantalla completa
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      ref="galleryContainer"
      class="product-gallery-layout position-relative h-100"
      :style="[
        fullScreen && !isMobileSize ? 'grid-template-columns: auto 1fr' : null,
      ]"
    >
      <div
        v-if="thumbsCount > 1"
        class="product-gallery-thumbs-column mw-100 mb-lg-0 mt-1 mt-lg-0 order-2 order-lg-0"
        :class="[fullScreen && !isMobileSize ? 'flex-shrink-0' : null]"
      >
        <router-link
          v-if="props.product.brand?.getImageSm() || ''"
          class="thumb-brand mb-1 d-flex align-items-center pointer me-auto mx-lg-auto"
          target="_blank"
          :to="product.brand?.resolveSearchRoute($router) || ''"
        >
          <img
            v-if="!brandError"
            width="50"
            height="50"
            :src="props.product.brand?.getImageSm() || ''"
            :alt="product.brand?.name || ''"
            @error="brandError = true"
          />
          <span v-else-if="product.brand">
            {{ product.brand.name }}
          </span>
        </router-link>
        <swiper
          class="product-gallery-thumbs"
          :style="{
            height: thumbsContainerHeight,
          }"
          :space-between="isMobileSize ? 6 : desktopThumbSpaceBetween"
          :slides-per-view="thumbsSlidesPerView"
          :watch-slides-progress="true"
          :slide-to-clicked-slide="true"
          :watch-overflow="true"
          :modules="modules"
          :direction="isMobileSize ? 'horizontal' : 'vertical'"
          @swiper="setThumbsSwiper"
        >
          <swiper-slide
            v-for="(url, index) in thumbImages"
            :key="index"
            class="d-flex"
          >
            <img
              :src="url || ''"
              width="60"
              height="60"
              alt="Imagen pequeña de producto"
            />
            <div class="thumb-border"></div>
          </swiper-slide>
          <swiper-slide v-if="product.video_link">
            <div
              class="d-block w-100 h-100 d-flex align-items-center justify-content-center"
            >
              <i class="bi bi-youtube text-danger fs-1"></i>
            </div>
            <div class="thumb-border"></div>
          </swiper-slide>
        </swiper>
      </div>
      <swiper
        class="product-gallery order-1"
        :class="[
          fullScreen && !isMobileSize && thumbsCount > 1 ? 'flex-grow-1' : null,
        ]"
        :style="{
          '--swiper-navigation-color': 'var(--bs-info)',
          '--swiper-pagination-color': 'var(--bs-info)',
        }"
        :loop="thumbsCount >= responsiveSlideCount && thumbsCount > 1"
        :lazy="false"
        :pagination="{
          clickable: true,
        }"
        :space-between="5"
        :navigation="true"
        :modules="modules"
        :thumbs="{ swiper: thumbsSwiper }"
        :keyboard="true"
        @swiper="setMainSwiper"
        @slide-change="onSlideChange"
      >
        <swiper-slide
          v-for="(url, index) in largeImages"
          :key="index"
          class="px-0 px-lg-3"
          :class="{ pointer: !fullScreen }"
          @click="toggleGalleryModal"
        >
          <img
            :src="url"
            :loading="isImageCover(url) ? 'eager' : 'lazy'"
            width="450"
            height="450"
            :fetchpriority="isImageCover(url) ? 'hight' : 'low'"
            :alt="
              isImageCover(url) && product.name
                ? product.name
                : 'Imagen de producto'
            "
          />
          <div
            v-if="!isImageCover(url)"
            class="swiper-lazy-preloader swiper-lazy-preloader-info"
          ></div>
        </swiper-slide>
        <swiper-slide v-if="product.video_link" :lazy="true">
          <div ref="videoSlideCt" class="w-100 h-100 px-4">
            <iframe
              v-if="showVideoSlide"
              :src="product.video_link"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerpolicy="strict-origin-when-cross-origin"
              allowfullscreen
            ></iframe>
          </div>
        </swiper-slide>
      </swiper>
      <div
        v-if="mounted"
        class="position-absolute end-0 0 z-2 d-flex flex-lg-column gap-1 mb-2"
        :class="[isMobileSize ? 'top-0' : 'bottom-0']"
      >
        <product-share-button :product="product" />
        <product-wishlist-button v-if="isUserLogged" :product="product" />
      </div>
    </div>
  </section>
</template>

<style lang="scss">
@use "@/assets/scss/bootstrap-helpers" as *;

$mobileHeight: 250px;
$desktopHeight: 450px;

:root {
  --thumb-size: 50px;
  --thumb-count: 6;
}

.product-gallery-layout {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;

  @include media-breakpoint-up(md) {
    flex-direction: row;
    align-items: flex-start;
  }
}

.product-gallery-thumbs-column {
  width: 100%;

  @include media-breakpoint-up(md) {
    width: calc(var(--thumb-size) + 2px);
    flex: 0 0 calc(var(--thumb-size) + 2px);
  }
}

.product-gallery,
.lcp-cover {
  height: auto;
  width: 100%;
  max-width: 100vw; // evita que crezca inesperadamente por algún error de width
  .swiper-button-prev::after,
  .swiper-button-next::after {
    color: var(--bs-red);
    font-family: bootstrap-icons;
    display: inline-block;
    font-size: 3.5em;
    vertical-align: middle;
    font-weight: bolder;
    @include media-breakpoint-up(md) {
      font-size: 2.5em;
    }
  }
  .swiper-button-prev {
    left: 3px;
    @include media-breakpoint-up(md) {
      left: 0px;
    }
    &::after {
      content: "\F12C";
    }
  }
  .swiper-button-next {
    right: 3px;
    @include media-breakpoint-up(md) {
      right: 0px;
    }
    &::after {
      content: "\F135";
    }
  }
}
.product-gallery .swiper-slide {
  display: flex;
  justify-content: center;
  align-items: center;
  height: $mobileHeight;
  @include media-breakpoint-up(md) {
    height: $desktopHeight;
  }
}
.modal-fullscreen {
  .grid {
    height: 100% !important;
    .product-gallery {
      height: 100% !important;
      .swiper-slide {
        height: 100% !important;
      }
    }
  }
  .product-gallery .swiper-slide img {
    width: auto;
    height: auto;
  }
}
.product-gallery .swiper-slide {
  img {
    display: block;
    width: $mobileHeight;
    height: $mobileHeight;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    @include media-breakpoint-up(md) {
      width: $desktopHeight;
      height: $desktopHeight;
    }
  }
  iframe {
    width: 100%;
    height: 100%;
  }
}
.product-gallery-thumbs {
  width: 100%;
  // height: calc(var(--thumb-size) + 2px);
  @include media-breakpoint-up(md) {
    width: calc(var(--thumb-size) + 2px);
    // height: auto;
    // max-height: calc(var(--thumb-size) * var(--thumb-count));
  }
  overflow: hidden;
}
.product-gallery-thumbs .swiper-wrapper {
  align-items: center;

  @include media-breakpoint-up(md) {
    align-items: flex-start;
    flex-direction: column;
  }
}
.product-gallery-thumbs .swiper-slide {
  width: calc(var(--thumb-size) + 2px) !important;
  height: calc(var(--thumb-size) + 2px) !important;
  flex: 0 0 calc(var(--thumb-size) + 2px);
  position: relative;
  padding-top: 2px;
  @include media-breakpoint-up(md) {
    padding-top: 0px;
  }
  img,
  iframe {
    display: block;
    width: var(--thumb-size);
    height: var(--thumb-size);
    object-fit: contain;
    margin: auto;
  }
  iframe {
    pointer-events: none;
  }
  &:hover,
  &.swiper-slide-thumb-active {
    .thumb-border {
      height: 100%;
      border-top-width: 2px;
      transition: all ease-in-out 0.2s;
      @include media-breakpoint-up(md) {
        width: 100%;
        border-right-width: 2px;
      }
    }
  }
}
.thumb-border {
  position: absolute;
  border-top: 0px solid var(--bs-info);
  width: 100%;
  height: 2px;
  top: 0;
  right: 0;
  background-color: rgba(#bcbcbc, 0.3);
  z-index: 1;
  @include media-breakpoint-up(md) {
    border-top: none;
    border-right: 0px solid var(--bs-info);
    width: 2px;
    height: 100%;
  }
}
.thumb-brand {
  display: block;
  width: var(--thumb-size);
  height: var(--thumb-size);
  img {
    width: auto;
    height: auto;
    max-width: 100%;
    max-height: 100%;
  }
  &:hover {
    transition: 0.2s;
    transform: scale(1.07);
  }
  position: absolute;
  z-index: 10;
  bottom: calc(var(--thumb-size) + 10px);
  @include media-breakpoint-up(md) {
    position: static;
  }
}
</style>
